import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { useEnvironmentConfiguration } from '@ori/environment';
import { SWRConfig } from '@ori/fetching';
import type { Translations } from '@ori/i18n';
import { TranslationsNamespaceProvider, TranslationsProvider, useTranslationsWithNamespaces } from '@ori/i18n';
import { TestIdProvider } from '@ori/testid-generator';
import type { FC } from 'react';
import { Suspense } from 'react';

import { APP_NAME, APP_TEAM_NAME, defaultTranslations, swrConfig, translationsNamespace } from '../constants';
import { ApiProvider } from './ApiProvider';
import { NovageSkincareAdvisor } from './NovageSkincareAdvisor';

export interface NovageSkincareAdvisorControllerProps {
  /** Translations for NovageSkincareAdvisor. */
  translations?: Translations | undefined;
}

/**
 * Accepts config data, fetches data for FeedbackBenefitDialog and renders it if
 * OpenFeedbackBenefitDialog event is dispatched
 */
export const NovageSkincareAdvisorController: FC<NovageSkincareAdvisorControllerProps> = ({ translations }) => {
  // istanbul ignore next
  const { tenant = '' } = useEnvironmentConfiguration();
  const { language } = useTranslationsWithNamespaces();
  const { staticApiUrl } = useServiceUrls(['staticApi']);
  const { token } = useToken();

  return (
    <TestIdProvider
      team={APP_TEAM_NAME}
      project={APP_NAME}
    >
      <SWRConfig value={swrConfig}>
        <Suspense fallback={null}>
          <ApiProvider>
            <TranslationsProvider
              language={language}
              defaultTranslationsWithNamespaces={{ [translationsNamespace]: defaultTranslations }}
              translationsWithNamespaces={translations ? { [translationsNamespace]: translations } : undefined}
              // eslint-disable-next-line no-console -- we need to log errors
              clientFetchConfiguration={{ tenant, token, baseUrl: staticApiUrl, onError: console.error }}
            >
              <TranslationsNamespaceProvider namespace={translationsNamespace}>
                <NovageSkincareAdvisor />
              </TranslationsNamespaceProvider>
            </TranslationsProvider>
          </ApiProvider>
        </Suspense>
      </SWRConfig>
    </TestIdProvider>
  );
};
